import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-emerald-700 hover:text-emerald-800 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-start space-x-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Shield className="w-8 h-8 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
              <p className="text-gray-600">Delinam Foundation</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-gray-200 shadow-lg">
              <CardContent className="p-8 lg:p-12">
                <div className="prose prose-gray max-w-none">
                  {/* Introduction */}
                  <div className="mb-8">
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Delinam Foundation is committed to protecting your privacy.
                    </p>
                    <p className="text-gray-600 leading-relaxed mt-4">
                      This Privacy Policy explains how we collect, use, and safeguard personal 
                      information submitted through our website.
                    </p>
                  </div>

                  {/* Information We Collect */}
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      Information We Collect
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      We may collect personal information such as your name, email address, phone 
                      number, and message when you contact us or express interest in our work.
                    </p>
                  </div>

                  {/* How We Use Your Information */}
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      How We Use Your Information
                    </h2>
                    <p className="text-gray-700 mb-3">We use your information solely to:</p>
                    <ul className="list-none space-y-2 ml-0">
                      <li className="flex items-start text-gray-700">
                        <span className="text-emerald-600 mr-3 mt-1">•</span>
                        <span>Respond to inquiries</span>
                      </li>
                      <li className="flex items-start text-gray-700">
                        <span className="text-emerald-600 mr-3 mt-1">•</span>
                        <span>Communicate about our programs and initiatives</span>
                      </li>
                      <li className="flex items-start text-gray-700">
                        <span className="text-emerald-600 mr-3 mt-1">•</span>
                        <span>Improve our services and outreach</span>
                      </li>
                    </ul>
                  </div>

                  {/* Data Protection */}
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      Data Protection
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      We take reasonable technical and organizational measures to protect your 
                      personal information against unauthorized access, loss, or misuse.
                    </p>
                  </div>

                  {/* Third Parties */}
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      Third Parties
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      We do not sell, trade, or rent your personal information to third parties.
                    </p>
                  </div>

                  {/* Your Rights */}
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      Your Rights
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      You may request access, correction, or deletion of your personal data by 
                      contacting us at{' '}
                      <a 
                        href="mailto:info@delinamfoundation.org" 
                        className="text-emerald-700 hover:text-emerald-800 font-medium underline"
                      >
                        info@delinamfoundation.org
                      </a>
                      .
                    </p>
                  </div>

                  {/* Updates */}
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      Updates
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      This policy may be updated from time to time. Any changes will be posted on this page.
                    </p>
                  </div>

                  {/* Contact */}
                  <div className="bg-emerald-50 border-l-4 border-emerald-600 p-6 rounded-r-lg">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                      <Mail className="w-6 h-6 mr-2 text-emerald-600" />
                      Contact
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-2">
                      For privacy-related questions, contact:
                    </p>
                    <a 
                      href="mailto:info@delinamfoundation.org"
                      className="text-emerald-700 hover:text-emerald-800 font-semibold text-lg inline-flex items-center"
                    >
                      <Mail className="w-5 h-5 mr-2" />
                      info@delinamfoundation.org
                    </a>
                  </div>

                  {/* Last Updated */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <p className="text-sm text-gray-500 text-center">
                      Last updated: {new Date().toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
